import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, MapPin, CalendarClock, HelpCircle, MonitorSmartphone, RotateCcw, MessageCircle, ListChecks } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import MobileBottomNav from "@/components/MobileBottomNav";
import { whatsappLink } from "@/lib/faro";
import { useSeo, JsonLd, SITE_URL, ogFromAsset } from "@/lib/seo";
import { LazyMap } from "@/components/LazyMap";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AuthorityNote } from "@/components/AuthorityNote";
import PointsRegistrationForm from "@/components/PointsRegistrationForm";

const WA = whatsappLink("34637570075", "Hola, necesito hacer el ciclo adicional de 4 horas para volver a presentarme a la prueba de recuperación del carnet");
const AULA = "Centro Comercial 3 Plaza América, Local 12, 36211 Vigo";
const AULA_MAP = "Autoescuelas Faro, Centro Comercial 3 Plaza América, Vigo";

const FAQ = [
  { q: "¿Qué es el ciclo adicional?", a: "Es un ciclo formativo de 4 horas que debe realizar el alumno que, tras aprovechar el curso de recuperación del carnet por puntos (tipo B1), suspende o no se presenta a la prueba de control de conocimientos de Tráfico. Sin el ciclo adicional no puedes presentarte a la siguiente convocatoria." },
  { q: "¿Cuántas convocatorias tengo para aprobar la prueba?", a: "Tres. Si suspendes o no te presentas a la primera, harás el ciclo adicional convocatoria 2. Si vuelve a ocurrir en la segunda, harás el ciclo adicional convocatoria 3. Si agotas las tres convocatorias sin aprobar, deberás realizar de nuevo el curso completo de recuperación del carnet (tipo B1)." },
  { q: "¿Cuánto dura y en qué modalidad se hace?", a: "Dura 4 horas y puedes hacerlo presencial en nuestra aula de Plaza América (Vigo) u online, con 4 horas de visionado de contenidos desde el móvil, la tablet o el ordenador." },
  { q: "¿Cuándo puedo empezar? ¿Hay que esperar a una convocatoria?", a: "No. El ciclo adicional es individual y totalmente a demanda: se puede programar de un día para otro. Tú decides cuándo empiezas y qué plazo quieres para completar las 4 horas." },
  { q: "¿Cuánto cuesta el ciclo adicional en Vigo?", a: "El precio es a consultar. Escríbenos por WhatsApp al 637 570 075 o llama al 986 29 24 55 y te lo confirmamos al momento, junto con la primera fecha disponible." },
  { q: "¿El ciclo adicional online es válido?", a: "Sí. La modalidad online consiste en 4 horas de visionado de contenidos desde cualquier dispositivo, con validez oficial para volver a presentarte a la prueba de Tráfico." },
  { q: "¿Qué pasa si suspendo las tres convocatorias?", a: "Tendrás que realizar de nuevo el curso completo de recuperación del carnet (20 horas, tipo B1) antes de disponer de nuevas convocatorias. También lo impartimos en nuestro centro de Plaza América." },
  { q: "¿Me preparáis para la prueba de Tráfico?", a: "Sí. Además del ciclo, te damos acceso a nuestros test para que llegues a la prueba de control de conocimientos con garantías de aprobar." },
];

export default function CicloAdicionalPage() {
  useSeo({
    title: "Ciclo Adicional de 4 h en Vigo | Recupera tu Carnet",
    description: "Ciclo adicional de 4 horas en Vigo para volver a presentarte a la prueba de recuperación del carnet. Presencial u online, programable de un día para otro.",
    canonical: `${SITE_URL}/ciclo-adicional-vigo`,
    ogImage: ogFromAsset("/assets/curso-recuperacion-puntos-vigo-plaza-america.webp"),
    ogImageAlt: "Aula del ciclo adicional de 4 horas en Plaza América, Vigo",
  });

  return (
    <div className="pb-[calc(env(safe-area-inset-bottom)+72px)] sm:pb-0" data-testid="ciclo-adicional-page">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Course",
        name: "Ciclo adicional de recuperación del permiso de conducir (4 horas)",
        description: "Ciclo formativo de 4 horas para quien suspende o no se presenta a la prueba tras el curso de recuperación del carnet. En Vigo, presencial u online.",
        provider: {
          "@type": ["LocalBusiness", "EducationalOrganization"],
          "@id": `${SITE_URL}/autoescuela-plaza-america#business`,
          name: "Autoescuelas Faro",
          url: SITE_URL,
          identifier: {
            "@type": "PropertyValue",
            name: "Autorización DGT",
            value: "360024",
          },
        },
        hasCourseInstance: { "@type": "CourseInstance", courseMode: "Blended" },
        locationCreated: { "@type": "Place", name: "Autoescuelas Faro - Plaza América", address: AULA },
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }} />

      <Header />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-1">
        <Breadcrumbs items={[{ label: "Permiso por puntos", to: "/permiso-por-puntos-vigo" }, { label: "Ciclo adicional" }]} />
      </div>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <span className="eyebrow">Centro autorizado DGT en Vigo</span>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">
          Ciclo adicional en Vigo: vuelve a examen cuanto antes
        </h1>
        <p className="mt-4 text-lg text-[var(--faro-text-soft)]">
          ¿Hiciste el <strong>curso de recuperación del carnet</strong> (tipo B1 o B3) y has
          <strong> suspendido</strong> (o no te presentaste a) la prueba de Tráfico? Para la siguiente convocatoria necesitas
          un <strong>ciclo adicional de 4 horas</strong>. Lo hacemos <strong>a tu medida</strong>: individual, presencial u online, y
          programable <strong>de un día para otro</strong>.
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <a href="#reserva" className="btn-pill btn-primary text-sm" data-testid="ca-cta-reserva">
            <CalendarClock size={15} /> Reserva tu ciclo
          </a>
          <a href={WA} target="_blank" rel="noreferrer" className="btn-pill btn-outline text-sm" data-testid="ca-cta-wa">
            <MessageCircle size={15} /> Pedir fecha por WhatsApp
          </a>
          <Link to="/curso-sensibilizacion-vigo" className="btn-pill btn-outline text-sm" data-testid="ca-cta-carnet">
            Ver curso completo (20 h)
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { icon: Clock, k: "4 h", v: "de formación en total" },
            { icon: MonitorSmartphone, k: "Online", v: "o presencial, tú eliges" },
            { icon: CalendarClock, k: "A demanda", v: "individual, de un día para otro" },
            { icon: RotateCcw, k: "3", v: "convocatorias para la prueba" },
          ].map((f) => (
            <div key={f.k} className="rounded-2xl bg-[var(--faro-blue)] text-white p-4">
              <f.icon size={18} className="text-[var(--faro-yellow)]" />
              <div className="font-display font-black text-2xl mt-2">{f.k}</div>
              <div className="text-xs text-white/80 mt-0.5 leading-snug">{f.v}</div>
            </div>
          ))}
        </div>

        <section className="mt-10">
          <h2 className="font-display font-black text-2xl sm:text-3xl flex items-center gap-2">
            <ListChecks className="text-[var(--faro-red)]" size={26} /> Cómo funcionan las convocatorias
          </h2>
          <p className="mt-3 text-[var(--faro-text-soft)]">
            Tras realizar con aprovechamiento el curso de recuperación del carnet (tipo B1 o B3),
            dispones de <strong>3 convocatorias</strong> para superar la prueba de control de conocimientos:
          </p>
          <ol className="mt-4 space-y-3">
            {[
              { n: "1", t: "Primera convocatoria", d: "Si eres declarado no apto o no te presentas, para volver a examinarte debes realizar el ciclo adicional convocatoria 2 (4 h)." },
              { n: "2", t: "Segunda convocatoria", d: "Si vuelves a ser declarado no apto o no te presentas, necesitarás el ciclo adicional convocatoria 3 (otras 4 h)." },
              { n: "3", t: "Tercera convocatoria", d: "Si agotas las tres convocatorias sin aprobar, deberás realizar un nuevo curso completo de recuperación del carnet de 20 h." },
            ].map((s) => (
              <li key={s.n} className="flex gap-3 rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-4">
                <span className="w-8 h-8 rounded-full bg-[var(--faro-red)] text-white font-display font-black flex items-center justify-center shrink-0">{s.n}</span>
                <div>
                  <div className="font-bold text-[var(--faro-blue-deep)]">{s.t}</div>
                  <p className="text-sm text-[var(--faro-text-soft)] mt-0.5">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-8">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Modalidad, fechas y precio</h2>
          <ul className="mt-3 space-y-2 text-[var(--faro-text-soft)]">
            <li className="flex gap-2"><Clock size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" /><span><strong>Duración:</strong> 4 horas de formación.</span></li>
            <li className="flex gap-2"><MonitorSmartphone size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" /><span><strong>Modalidad:</strong> presencial en cualquiera de nuestros centros de Vigo, Coruxo, Nigrán, Panxón o A Cañiza, u <strong>online</strong>, con 4 horas de visionado de contenidos desde el móvil, la tablet o el ordenador.</span></li>
            <li className="flex gap-2"><CalendarClock size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" /><span><strong>Fechas:</strong> totalmente a demanda. El curso es <strong>individual</strong> y se puede programar de un día para otro: tú decides cuándo empiezas y qué plazo quieres para completar las 4 horas.</span></li>
            <li className="flex gap-2"><MessageCircle size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" /><span><strong>Precio:</strong> a consultar — escríbenos por <a href={WA} target="_blank" rel="noreferrer" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">WhatsApp (637 570 075)</a> o llama al <a href="tel:+34986292455" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">986 29 24 55</a>.</span></li>
          </ul>
        </section>

        <section className="mt-8">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Dónde se imparte</h2>
          <p className="mt-3 text-[var(--faro-text-soft)] flex items-start gap-2">
            <MapPin size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" />
            <span><strong>{AULA}</strong> — centro autorizado por la DGT (o desde casa en la modalidad online).</span>
          </p>
          <div className="mt-4 rounded-2xl relative overflow-hidden border border-[rgba(30,42,143,0.12)] h-64">
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(AULA_MAP)}`} target="_blank" rel="noopener noreferrer" className="block w-full h-full" aria-label="Abrir la ubicacion en Google Maps"><img src="/img/mapa-plaza-america.jpg" alt="Mapa de la zona de Plaza America en Vigo" width="1024" height="420" className="w-full h-full object-cover" /></a><span className="pointer-events-none absolute left-2 top-2 rounded bg-white px-3 py-1.5 text-xs font-medium text-[#202124] shadow">Abrir en Maps &#8599;</span><div className="pointer-events-none absolute left-1/2 top-1/2 flex -translate-x-3 -translate-y-full items-end gap-1"><svg width="28" height="36" viewBox="0 0 24 32" aria-hidden="true"><path d="M12 0C5.4 0 0 5.3 0 12c0 8.9 12 20 12 20s12-11.1 12-20C24 5.3 18.6 0 12 0z" fill="#EA4335" /><circle cx="12" cy="12" r="4.5" fill="#fff" /></svg><span className="mb-1 rounded bg-white/95 px-2 py-1 text-[11px] font-medium text-[#202124] shadow">Autoescuelas Faro - Plaza América</span></div><span className="absolute bottom-1 right-2 text-[10px] bg-white/80 px-1 rounded">&copy; OpenStreetMap contributors</span>
          </div>
        </section>

        <section className="mt-8">
          <h2 className="font-display font-black text-2xl sm:text-3xl">¿No es tu caso?</h2>
          <ul className="mt-3 list-disc pl-5 space-y-1 text-[var(--faro-text-soft)]">
            <li>Si aún no has hecho el curso de 20 h, empieza por <Link to="/curso-sensibilizacion-vigo" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">recuperar el carnet en Vigo</Link>.</li>
            <li>Si conservas el carnet y solo quieres recuperar puntos, lo tuyo es el <Link to="/recuperacion-de-puntos-vigo" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">curso parcial (1 día + 3 h online)</Link>.</li>
            <li>¿Retirada judicial? Mira el <Link to="/curso-sensibilizacion-vigo" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">curso de sensibilización (B2 y B3)</Link>.</li>
          </ul>
        </section>

        <AuthorityNote />

        <section className="mt-10" id="reserva" data-testid="ca-inscripcion">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Reserva tu ciclo adicional</h2>
          <p className="mt-3 text-[var(--faro-text-soft)]">
            Deja tus datos y te llamamos hoy mismo para fijar la fecha (lo programamos de un día para otro).
          </p>
          <div className="mt-5">
            <PointsRegistrationForm defaultCourseType="CICLO_ADICIONAL" formId="ciclo-formulario" sideTitle="Reserva tu ciclo adicional" />
          </div>
        </section>

        <section className="mt-10" id="faq">
          <h2 className="font-display font-black text-2xl sm:text-3xl flex items-center gap-2">
            <HelpCircle className="text-[var(--faro-red)]" size={26} /> Preguntas frecuentes
          </h2>
          <div className="mt-4 space-y-2" data-testid="ca-faq">
            {FAQ.map((f) => (
              <details key={f.q} className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] px-5 py-4 group">
                <summary className="cursor-pointer list-none font-bold text-[var(--faro-blue-deep)] flex items-center justify-between gap-3">
                  {f.q}
                  <span className="text-[var(--faro-red)] group-open:rotate-90 transition-transform shrink-0"><ArrowRight size={16} /></span>
                </summary>
                <p className="mt-2 text-sm text-[var(--faro-text-soft)]">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <div className="mt-10 rounded-2xl bg-[var(--faro-blue)] text-white p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--faro-yellow)]">Programación de un día para otro</div>
            <div className="font-display font-black text-xl mt-1">No pierdas tu próxima convocatoria</div>
            <p className="text-sm text-white/85 mt-1">Ciclo adicional de 4 h presencial u online · Centro autorizado DGT en Vigo desde 1980.</p>
          </div>
          <div className="flex flex-col gap-2">
            <a href="#reserva" className="btn-pill btn-primary text-sm justify-center" data-testid="ca-cta-final">
              <CalendarClock size={14} /> Reserva tu ciclo
            </a>
            <a href="tel:+34986292455" className="btn-pill bg-white text-[var(--faro-blue)] text-sm justify-center">
              Llamar al 986 29 24 55
            </a>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingWhatsapp />
      <MobileBottomNav />
    </div>
  );
}
